import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { QuestionMapping } from '../data/questionMapping';

interface ResultPDFProps {
  results: Record<string, any>;
  childInfo: any;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
    fontSize: 9,
  },
  header: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
    color: '#333',
  },
  questionNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#2c3e50',
  },
  section: {
    marginBottom: 15,
  },
  mainQuestion: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#34495e',
  },
  mainAnswer: {
    fontSize: 11,
    marginBottom: 10,
    color: '#7f8c8d',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#2c3e50',
  },
  subSectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#34495e',
    marginLeft: 5,
  },
  subSectionSubtitle: {
    fontSize: 9,
    fontStyle: 'italic',
    marginBottom: 8,
    color: '#7f8c8d',
    marginLeft: 5,
  },
  subQuestion: {
    fontSize: 10,
    marginBottom: 3,
    color: '#34495e',
    marginLeft: 10,
  },
  subAnswer: {
    fontSize: 10,
    marginBottom: 8,
    color: '#7f8c8d',
    marginLeft: 20,
  },
  userExample: {
    fontSize: 10,
    marginBottom: 5,
    color: '#e74c3c',
    fontStyle: 'italic',
  },
  userExampleText: {
    fontSize: 10,
    marginBottom: 10,
    color: '#e74c3c',
    marginLeft: 10,
  },
  lowRisk: {
    color: 'green',
  },
  moderateRisk: {
    color: 'orange',
  },
  highRisk: {
    color: 'red',
  },
  result: {
    position: 'absolute',
    top: 30,
    right: 30,
    fontSize: 14,
    fontWeight: 'bold',
    padding: 8,
    borderRadius: 5,
    color: '#ffffff',
  },
  passResult: {
    backgroundColor: '#27ae60',
  },
  failResult: {
    backgroundColor: '#e74c3c',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 20,
    right: 30,
    fontSize: 10,
    color: '#95a5a6',
  },
  // New styles for Question 1 layout
  behaviorSection: {
    marginBottom: 15,
  },
  behaviorTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2c3e50',
  },
  columnsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  column: {
    width: '48%',
  },
  zeroExamplesColumn: {
    border: '2px solid #27ae60',
    padding: 10,
    borderRadius: 5,
  },
  oneExamplesColumn: {
    border: '2px solid #e74c3c',
    padding: 10,
    borderRadius: 5,
  },
  columnTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#2c3e50',
  },
  questionItem: {
    marginBottom: 8,
  },
  questionText: {
    fontSize: 9,
    marginBottom: 3,
    color: '#34495e',
  },
  answerText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#7f8c8d',
  },
  yesAnswer: {
    color: '#27ae60',
  },
  noAnswer: {
    color: '#e74c3c',
  },
  // Enhanced styles for Most Often Choice
  mostOftenSection: {
    marginBottom: 15,
    border: '3px solid #3498db',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#ebf3fd',
  },
  mostOftenTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#2980b9',
  },
  mostOftenSubtitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#3498db',
  },
  mostOftenAnswer: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2980b9',
  },
  // New styles for Question 2 layout
  behaviorQuestionsSection: {
    marginBottom: 15,
    border: '2px solid #9b59b6',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f8f4ff',
  },
  behaviorQuestionsTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#8e44ad',
    textAlign: 'center',
  },
  behaviorQuestionItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#f0e6ff',
    borderRadius: 5,
  },
  behaviorQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#6c5ce7',
  },
  behaviorAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#8e44ad',
  },
  hearingTestSection: {
    marginBottom: 15,
    border: '2px solid #3498db',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f0f8ff',
  },
  hearingTestTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2980b9',
    textAlign: 'center',
  },
  hearingTestSubtitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#3498db',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  hearingTestItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#e3f2fd',
    borderRadius: 5,
  },
  hearingTestQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#1976d2',
  },
  hearingTestAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2980b9',
  },
  // New styles for Question 3 layout
  pretendPlaySection: {
    marginBottom: 15,
    border: '2px solid #9b59b6',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f8f4ff',
  },
  pretendPlayTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#8e44ad',
    textAlign: 'center',
  },
  pretendPlayItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#e8f4fd',
    borderRadius: 5,
  },
  pretendPlayQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#3498db',
  },
  pretendPlayAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2980b9',
  },
  // New styles for Question 4 layout
  climbingSection: {
    marginBottom: 15,
    border: '2px solid #27ae60',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f0fff4',
  },
  climbingTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2ecc71',
    textAlign: 'center',
  },
  climbingItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#e8f5e8',
    borderRadius: 5,
  },
  climbingQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#27ae60',
  },
  climbingAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2ecc71',
  },
  // New styles for Question 5 layout
  fingerMovementsSection: {
    marginBottom: 15,
  },
  fingerMovementsTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2c3e50',
  },
  fingerMovementsColumnsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  fingerMovementsColumn: {
    width: '48%',
  },
  greenColumn: {
    border: '2px solid #27ae60',
    padding: 10,
    borderRadius: 5,
  },
  yellowColumn: {
    border: '2px solid #f39c12',
    padding: 10,
    borderRadius: 5,
  },
  fingerMovementsColumnTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#2c3e50',
  },
  fingerMovementsItem: {
    marginBottom: 8,
  },
  fingerMovementsQuestionText: {
    fontSize: 9,
    marginBottom: 3,
    color: '#34495e',
  },
  fingerMovementsAnswerText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#7f8c8d',
  },
  greenAnswer: {
    color: '#27ae60',
  },
  yellowAnswer: {
    color: '#f39c12',
  },
  frequencySection: {
    marginBottom: 15,
    border: '2px solid #e67e22',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#fef9e7',
  },
  frequencyTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#d68910',
    textAlign: 'center',
  },
  frequencyItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#fdebd0',
    borderRadius: 5,
  },
  frequencyQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#e67e22',
  },
  frequencyAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#d68910',
  },
  // New styles for Question 6 layout
  pointingBehaviorsSection: {
    marginBottom: 15,
    border: '2px solid #9b59b6',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f8f4ff',
  },
  pointingBehaviorsTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#8e44ad',
    textAlign: 'center',
  },
  pointingBehaviorsItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#f0e6ff',
    borderRadius: 5,
  },
  pointingBehaviorsQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#6c5ce7',
  },
  pointingBehaviorsAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#8e44ad',
  },
  showMeSection: {
    marginBottom: 15,
    border: '2px solid #3498db',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f0f8ff',
  },
  showMeTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2980b9',
    textAlign: 'center',
  },
  showMeItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#e3f2fd',
    borderRadius: 5,
  },
  showMeQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#1976d2',
  },
  showMeAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2980b9',
  },
  // New styles for Question 7 layout
  imitationBehaviorsSection: {
    marginBottom: 15,
    border: '2px solid #9b59b6',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f8f4ff',
  },
  imitationBehaviorsTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#8e44ad',
    textAlign: 'center',
  },
  imitationBehaviorsItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#f0e6ff',
    borderRadius: 5,
  },
  imitationBehaviorsQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#6c5ce7',
  },
  imitationBehaviorsAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#8e44ad',
  },
  additionalChecksSection: {
    marginBottom: 15,
    border: '2px solid #3498db',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f0f8ff',
  },
  additionalChecksTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2980b9',
    textAlign: 'center',
  },
  additionalChecksItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#e3f2fd',
    borderRadius: 5,
  },
  additionalChecksQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#1976d2',
  },
  additionalChecksAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2980b9',
  },
  // New styles for Question 8 layout
  interestCheckSection: {
    marginBottom: 15,
    border: '2px solid #9b59b6',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f8f4ff',
  },
  interestCheckTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#8e44ad',
    textAlign: 'center',
  },
  interestCheckItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#f0e6ff',
    borderRadius: 5,
  },
  interestCheckQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#6c5ce7',
  },
  interestCheckAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#8e44ad',
  },
  socialBehaviorsSection: {
    marginBottom: 15,
    border: '2px solid #3498db',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f0f8ff',
  },
  socialBehaviorsTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2980b9',
    textAlign: 'center',
  },
  socialBehaviorsItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#e3f2fd',
    borderRadius: 5,
  },
  socialBehaviorsQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#1976d2',
  },
  socialBehaviorsAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2980b9',
  },
  responseCheckSection: {
    marginBottom: 15,
    border: '2px solid #e67e22',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#fef9e7',
  },
  responseCheckTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#d68910',
    textAlign: 'center',
  },
  responseCheckItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#fdebd0',
    borderRadius: 5,
  },
  responseCheckQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#e67e22',
  },
  responseCheckAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#d68910',
  },
  // New styles for Question 8 follow-up question
  followupQuestionSection: {
    marginBottom: 15,
    border: '2px solid #3498db',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f0f8ff',
  },
  followupQuestionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2980b9',
    textAlign: 'center',
  },
  followupQuestionItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#e3f2fd',
    borderRadius: 5,
  },
  followupQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#1976d2',
  },
  followupQuestionAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2980b9',
  },
  // New styles for Question 9 layout
  sharingBehaviorsSection: {
    marginBottom: 15,
    border: '2px solid #9b59b6',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f8f4ff',
  },
  sharingBehaviorsTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#8e44ad',
    textAlign: 'center',
  },
  sharingBehaviorsItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#f0e6ff',
    borderRadius: 5,
  },
  sharingBehaviorsQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#6c5ce7',
  },
  sharingBehaviorsAnswerText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#8e44ad',
  },
  sharingFollowupSection: {
    marginBottom: 15,
    border: '2px solid #3498db',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f0f8ff',
  },
  sharingFollowupTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2980b9',
    textAlign: 'center',
  },
  sharingFollowupItem: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#e3f2fd',
    borderRadius: 5,
  },
  sharingFollowupQuestionText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#1976d2',
  },
     sharingFollowupAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#2980b9',
   },
   // New styles for Question 12 noise sensitivity section
   noiseSensitivitySection: {
     marginBottom: 15,
     border: '2px solid #e67e22',
     padding: 10,
     borderRadius: 8,
     backgroundColor: '#fef9e7',
   },
   noiseSensitivityTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 8,
     color: '#d68910',
     textAlign: 'center',
   },
   noiseSensitivityItem: {
     marginBottom: 4,
     padding: 4,
     backgroundColor: '#fdebd0',
     borderRadius: 3,
   },
   noiseSensitivityQuestionText: {
     fontSize: 9,
     marginBottom: 2,
     color: '#e67e22',
   },
   noiseSensitivityAnswerText: {
     fontSize: 9,
     fontWeight: 'bold',
     color: '#d68910',
   },
   // New styles for Question 13 layout
   walkingBehaviorsSection: {
     marginBottom: 15,
     border: '2px solid #9b59b6',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f8f4ff',
   },
   walkingBehaviorsTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#8e44ad',
     textAlign: 'center',
   },
   walkingBehaviorsItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#f0e6ff',
     borderRadius: 5,
   },
   walkingBehaviorsQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#6c5ce7',
   },
   walkingBehaviorsAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#8e44ad',
   },
   // New styles for Question 14 layout
   eyeContactBehaviorsSection: {
     marginBottom: 15,
     border: '2px solid #9b59b6',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f8f4ff',
   },
   eyeContactBehaviorsTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#8e44ad',
     textAlign: 'center',
   },
   eyeContactBehaviorsItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#f0e6ff',
     borderRadius: 5,
   },
   eyeContactBehaviorsQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#6c5ce7',
   },
   eyeContactBehaviorsAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#8e44ad',
   },
   eyeContactFollowupSection: {
     marginBottom: 15,
     border: '2px solid #3498db',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f0f8ff',
   },
   eyeContactFollowupTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#2980b9',
     textAlign: 'center',
   },
   eyeContactFollowupItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#e3f2fd',
     borderRadius: 5,
   },
   eyeContactFollowupQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#1976d2',
   },
   eyeContactFollowupAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#2980b9',
   },
   // New styles for Question 15 layout
   copyingBehaviorsSection: {
     marginBottom: 15,
     border: '2px solid #9b59b6',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f8f4ff',
   },
   copyingBehaviorsTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#8e44ad',
     textAlign: 'center',
   },
   copyingBehaviorsItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#f0e6ff',
     borderRadius: 5,
   },
   copyingBehaviorsQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#6c5ce7',
   },
   copyingBehaviorsAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#8e44ad',
   },
   // New styles for Question 17 layout
   attentionSeekingBehaviorsSection: {
     marginBottom: 15,
     border: '2px solid #9b59b6',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f8f4ff',
   },
   attentionSeekingBehaviorsTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#8e44ad',
     textAlign: 'center',
   },
   attentionSeekingBehaviorsItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#f0e6ff',
     borderRadius: 5,
   },
   attentionSeekingBehaviorsQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#6c5ce7',
   },
   attentionSeekingBehaviorsAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#8e44ad',
   },
   // New styles for Question 18 layout
   commandUnderstandingWithCluesSection: {
     marginBottom: 15,
     border: '2px solid #9b59b6',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f8f4ff',
   },
   commandUnderstandingWithCluesTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#8e44ad',
     textAlign: 'center',
   },
   commandUnderstandingWithCluesItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#f0e6ff',
     borderRadius: 5,
   },
   commandUnderstandingWithCluesQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#6c5ce7',
   },
   commandUnderstandingWithCluesAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#8e44ad',
   },
   commandUnderstandingWithoutCluesSection: {
     marginBottom: 15,
     border: '2px solid #3498db',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f0f8ff',
   },
   commandUnderstandingWithoutCluesTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#2980b9',
     textAlign: 'center',
   },
   commandUnderstandingWithoutCluesItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#e3f2fd',
     borderRadius: 5,
   },
   commandUnderstandingWithoutCluesQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#1976d2',
   },
   commandUnderstandingWithoutCluesAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#2980b9',
   },
   // New styles for Question 19 layout
   socialReferencingBehaviorsSection: {
     marginBottom: 15,
     border: '2px solid #9b59b6',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f8f4ff',
   },
   socialReferencingBehaviorsTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#8e44ad',
     textAlign: 'center',
   },
   socialReferencingBehaviorsItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#f0e6ff',
     borderRadius: 5,
   },
   socialReferencingBehaviorsQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#6c5ce7',
   },
   socialReferencingBehaviorsAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#8e44ad',
   },
   // New styles for Question 20 layout
   movementEnjoymentSection: {
     marginBottom: 15,
     border: '2px solid #3498db',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f0f8ff',
   },
   movementEnjoymentTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#2980b9',
     textAlign: 'center',
   },
   movementEnjoymentItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#e3f2fd',
     borderRadius: 5,
   },
   movementEnjoymentQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#1976d2',
   },
   movementEnjoymentAnswerText: {
     fontSize: 10,
     fontWeight: 'bold',
     color: '#2980b9',
   },
   movementReactionsSection: {
     marginBottom: 15,
     border: '2px solid #9b59b6',
     padding: 12,
     borderRadius: 8,
     backgroundColor: '#f8f4ff',
   },
   movementReactionsTitle: {
     fontSize: 11,
     fontWeight: 'bold',
     marginBottom: 10,
     color: '#8e44ad',
     textAlign: 'center',
   },
   movementReactionsItem: {
     marginBottom: 8,
     padding: 8,
     backgroundColor: '#f0e6ff',
     borderRadius: 5,
   },
   movementReactionsQuestionText: {
     fontSize: 10,
     marginBottom: 3,
     color: '#6c5ce7',
   },
       movementReactionsAnswerText: {
      fontSize: 10,
      fontWeight: 'bold',
      color: '#8e44ad',
    },
    // Professional Report Styles
    professionalHeader: {
      marginBottom: 15,
      borderBottom: '2px solid #2c3e50',
      paddingBottom: 10,
    },
         reportTitle: {
       fontSize: 16,
       fontWeight: 'bold',
       textAlign: 'left',
       color: '#2c3e50',
       marginBottom: 5,
     },
    reportSubtitle: {
      fontSize: 10,
      textAlign: 'center',
      color: '#7f8c8d',
      marginBottom: 5,
    },
    reportDate: {
      fontSize: 9,
      textAlign: 'left',
      color: '#7f8c8d',
    },
         centerLogoContainer: {
       position: 'absolute',
       top: 5,
       right: 20,
       width: 90,
       height: 80,
       zIndex: 1000,
     },
    centerLogo: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    patientInfoSection: {
      marginBottom: 12,
      border: '1px solid #bdc3c7',
      padding: 8,
      backgroundColor: '#f8f9fa',
    },
    patientInfoGrid: {
      marginTop: 5,
    },
    patientInfoRow: {
      flexDirection: 'row',
      marginBottom: 3,
    },
    patientInfoLabel: {
      fontSize: 8,
      fontWeight: 'bold',
      color: '#2c3e50',
      width: '20%',
    },
    patientInfoValue: {
      fontSize: 8,
      color: '#34495e',
      width: '30%',
      marginRight: 10,
    },
    assessmentSummarySection: {
      marginBottom: 12,
      border: '1px solid #bdc3c7',
      padding: 8,
      backgroundColor: '#f8f9fa',
    },
    summaryGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 5,
    },
    summaryItem: {
      width: '25%',
      marginBottom: 5,
    },
    summaryLabel: {
      fontSize: 8,
      fontWeight: 'bold',
      color: '#2c3e50',
      marginBottom: 2,
    },
    summaryValue: {
      fontSize: 9,
      fontWeight: 'bold',
      color: '#34495e',
    },
    detailedResultsSection: {
       marginBottom: 12,
       border: '1px solid #bdc3c7',
       padding: 8,
       backgroundColor: '#f8f9fa',
     },
     resultsGrid: {
       marginTop: 5,
     },
     resultsRow: {
       flexDirection: 'row',
       marginBottom: 8,
       justifyContent: 'space-between',
     },
     resultItem: {
       width: '18%',
       border: '1px solid #bdc3c7',
       borderRadius: 4,
       padding: 6,
       backgroundColor: '#ffffff',
       alignItems: 'center',
     },
     resultQuestionId: {
       fontSize: 8,
       fontWeight: 'bold',
       color: '#2c3e50',
       marginBottom: 3,
       textAlign: 'center',
     },
     resultStatus: {
       fontSize: 8,
       fontWeight: 'bold',
       paddingHorizontal: 4,
       paddingVertical: 1,
       borderRadius: 3,
     },
    passStatus: {
      backgroundColor: '#d5f4e6',
      color: '#27ae60',
    },
    failStatus: {
      backgroundColor: '#fadbd8',
      color: '#e74c3c',
    },
    clinicalNotesSection: {
      marginBottom: 12,
      border: '1px solid #bdc3c7',
      padding: 8,
      backgroundColor: '#f8f9fa',
    },
    clinicalNoteText: {
      fontSize: 10,
      color: '#34495e',
      marginBottom: 5,
      lineHeight: 1.3,
    },
    recommendationsSection: {
      marginBottom: 12,
      border: '1px solid #bdc3c7',
      padding: 8,
      backgroundColor: '#f8f9fa',
    },
    recommendationText: {
      fontSize: 8,
      color: '#34495e',
      marginBottom: 3,
      lineHeight: 1.3,
    },
    footer: {
      marginTop: 10,
      borderTop: '1px solid #bdc3c7',
      paddingTop: 8,
    },
         footerText: {
       fontSize: 7,
       color: '#7f8c8d',
       textAlign: 'center',
       marginBottom: 2,
       fontStyle: 'italic',
     },
                      logoContainer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        width: 40,
        height: 40,
        zIndex: 1000,
      },
      logo: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
      },
             copyrightContainer: {
         position: 'absolute',
         bottom: 20,
         left: 70,
         right: 20,
         zIndex: 1000,
         flexDirection: 'column',
         justifyContent: 'flex-start',
       },
                                copyrightText: {
             fontSize: 8,
             color: '#7f8c8d',
             textAlign: 'left',
             lineHeight: 1.2,
             marginBottom: 2,
             whiteSpace: 'normal',
           },
       
   });

const SimpleResultPDF: React.FC<ResultPDFProps> = ({ results, childInfo }) => {
  const childName = childInfo?.childName || "your child";
  
  // Function to personalize question text
  const personalizeQuestion = (questionText: string) => {
    return questionText
      .replace(/your child/gi, childName)
      .replace(/his or her/gi, childInfo?.gender === "male" ? "his" : "her")
      .replace(/he or she/gi, childInfo?.gender === "male" ? "he" : "she")
      .replace(/his\/her/gi, childInfo?.gender === "male" ? "his" : "her")
      .replace(/he\/she/gi, childInfo?.gender === "male" ? "he" : "she");
  };

  // Function to recursively find all responses in a question node
  const getAllResponsesFromNode = (questionNode: any, questionId: string): Array<{id: string, title: string, answer: string, type: string}> => {
    const responses: Array<{id: string, title: string, answer: string, type: string}> = [];
    const processedIds = new Set<string>();
    
    // Add main question response if it exists
    if (questionNode.answer && questionNode.answer !== null) {
      const questionMapping = QuestionMapping[questionId];
      if (questionMapping) {
        responses.push({
          id: questionId,
          title: personalizeQuestion(questionMapping.title),
          answer: questionNode.answer.toString().toUpperCase(),
          type: questionMapping.type
        });
        processedIds.add(questionId);
      }
    }
    
    // Recursively process children
    if (questionNode.children && Array.isArray(questionNode.children)) {
      questionNode.children.forEach((child: any) => {
        if (child.id && child.answer && child.answer !== null && !processedIds.has(child.id)) {
          const childMapping = QuestionMapping[child.id];
          if (childMapping) {
            responses.push({
              id: child.id,
              title: personalizeQuestion(childMapping.title),
              answer: child.answer.toString().toUpperCase(),
              type: childMapping.type
            });
            processedIds.add(child.id);
          }
        }
        
        // Recursively process nested children
        if (child.children && Array.isArray(child.children)) {
          const nestedResponses = getAllResponsesFromNode(child, child.id);
          // Only add nested responses that haven't been processed yet
          nestedResponses.forEach(response => {
            if (!processedIds.has(response.id)) {
              responses.push(response);
              processedIds.add(response.id);
            }
          });
        }
      });
    }
    
    return responses;
  };

  const generatePages = () => {
    const pages: JSX.Element[] = [];
    
    // Summary page
    const completedQuestions = Object.entries(results).filter(([_, result]) => result.completed);
    const passCount = completedQuestions.filter(([_, result]) => result.result === "pass").length;
    const failCount = completedQuestions.filter(([_, result]) => result.result === "fail").length;
    
                   pages.push(
        <Page key="summary" size="A4" style={styles.page}>
          {/* Professional Header */}
          <View style={styles.professionalHeader}>
            <Text style={styles.reportTitle}>M-CHAT-R Followup Assessment</Text> 
            <Text style={styles.reportDate}>Date: {new Date().getDate().toString().padStart(2, '0')} {new Date().toLocaleDateString('en-US', { month: 'short' }).toUpperCase()} {new Date().getFullYear()}</Text>
          </View>
          
          {/* Patient Information Section */}
          <View style={styles.patientInfoSection}>
            <Text style={styles.sectionTitle}>PATIENT INFORMATION</Text>
            <View style={styles.patientInfoGrid}>
              <View style={styles.patientInfoRow}>
                <Text style={styles.patientInfoLabel}>Name:</Text>
                <Text style={styles.patientInfoValue}>{childInfo?.childName || 'Not provided'}</Text>
                                 <Text style={styles.patientInfoLabel}>Date of Birth:</Text>
                 <Text style={styles.patientInfoValue}>{(() => {
                   if (childInfo?.dateOfBirth) {
                     const birthDate = new Date(childInfo.dateOfBirth);
                     const today = new Date();
                     const ageInMonths = (today.getFullYear() - birthDate.getFullYear()) * 12 + 
                                       (today.getMonth() - birthDate.getMonth());
                     
                     // Format date as DD/MM/YYYY
                     const day = birthDate.getDate().toString().padStart(2, '0');
                     const month = (birthDate.getMonth() + 1).toString().padStart(2, '0');
                     const year = birthDate.getFullYear();
                     
                     return `${day}/${month}/${year} (${ageInMonths} months)`;
                   }
                   return 'Not provided';
                 })()}</Text>
              </View>
              <View style={styles.patientInfoRow}>
                <Text style={styles.patientInfoLabel}>Gender:</Text>
                <Text style={styles.patientInfoValue}>{childInfo?.gender || 'Not provided'}</Text>
                <Text style={styles.patientInfoLabel}>Location:</Text>
                <Text style={styles.patientInfoValue}>{childInfo?.city || 'Not provided'}</Text>
              </View>
              <View style={styles.patientInfoRow}>
                <Text style={styles.patientInfoLabel}>Guardian:</Text>
                <Text style={styles.patientInfoValue}>{childInfo?.guardianName || 'Not provided'}</Text>
                <Text style={styles.patientInfoLabel}>Contact:</Text>
                <Text style={styles.patientInfoValue}>{childInfo?.guardianPhone || 'Not provided'}</Text>
              </View>
            </View>
          </View>
          
          {/* Assessment Summary */}
          <View style={styles.assessmentSummarySection}>
            <Text style={styles.sectionTitle}>ASSESSMENT SUMMARY</Text>
            <View style={styles.summaryGrid}>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Total Items:</Text>
                <Text style={styles.summaryValue}>{completedQuestions.length}/20</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Passed Items:</Text>
                <Text style={styles.summaryValue}>{passCount}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Failed Items:</Text>
                <Text style={styles.summaryValue}>{failCount}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Risk Level:</Text>
                <Text style={[
                  styles.summaryValue,
                  failCount >= 0 && failCount <= 3
                    ? styles.lowRisk
                    : failCount >= 4 && failCount <= 7
                    ? styles.moderateRisk
                    : styles.highRisk
                ]}>
                  { failCount >= 0 && failCount <= 3
                    ? 'LOW RISK'
                    : failCount >= 4 && failCount <= 7
                    ? 'MODERATE RISK'
                    : 'HIGH RISK'}
                </Text>
              </View>
            </View>
          </View>
          
          {/* Detailed Results */}
           <View style={styles.detailedResultsSection}>
             <Text style={styles.sectionTitle}>DETAILED RESULTS</Text>
             <View style={styles.resultsGrid}>
               {(() => {
                 const rows = [];
                 for (let i = 0; i < completedQuestions.length; i += 5) {
                   const rowQuestions = completedQuestions.slice(i, i + 5);
                   rows.push(
                     <View key={i} style={styles.resultsRow}>
                       {rowQuestions.map(([questionId, result]) => (
                         <View key={questionId} style={styles.resultItem}>
                           <Text style={styles.resultQuestionId}>{questionId}</Text>
                           <Text style={[
                             styles.resultStatus, 
                             result.result?.toLowerCase() === 'pass' ? styles.passStatus : styles.failStatus
                           ]}>
                             {result.result?.toUpperCase() || 'N/A'}
                           </Text>
                         </View>
                       ))}
                       {/* Fill empty spaces if less than 5 questions in the last row */}
                       {Array.from({ length: 5 - rowQuestions.length }, (_, index) => (
                         <View key={`empty-${index}`} style={styles.resultItem}>
                           <Text style={styles.resultQuestionId}></Text>
                           <Text style={styles.resultStatus}></Text>
                         </View>
                       ))}
                     </View>
                   );
                 }
                 return rows;
               })()}
             </View>
           </View>
          
          {/* Clinical Notes */}
          <View style={styles.clinicalNotesSection}>
            <Text style={styles.sectionTitle}>CLINICAL NOTES</Text>
            <Text style={styles.clinicalNoteText}>
              The M-CHAT-R is a validated screening tool for autism spectrum disorder (ASD) in children aged 16-30 months. 
              This assessment evaluates communication, social interaction, and behavioral patterns.
            </Text>
            <Text style={styles.clinicalNoteText}>
              {failCount >= 3 ? 
                'This child has failed 3 or more critical items, indicating elevated risk for ASD. Further comprehensive evaluation by a developmental specialist is strongly recommended.' :
                'This child has passed the majority of screening items. However, if clinical concerns persist, consider follow-up evaluation.'
              }
            </Text>
          </View>
          
          {/* Recommendations */}
          <View style={styles.recommendationsSection}>
            <Text style={styles.sectionTitle}>RECOMMENDATIONS</Text>
            <Text style={styles.recommendationText}>
              • {failCount >= 3 ? 'Immediate referral to developmental pediatrician or child psychiatrist' : 'Monitor developmental milestones'}
            </Text>
            <Text style={styles.recommendationText}>
              • {failCount >= 3 ? 'Comprehensive ASD evaluation including ADOS-2 and cognitive assessment' : 'Re-screen in 3-6 months if concerns persist'}
            </Text>
            <Text style={styles.recommendationText}>
              • {failCount >= 3 ? 'Early intervention services consultation' : 'Continue routine developmental surveillance'}
            </Text>
          </View>
          
          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              This screening tool is not diagnostic. Clinical judgment and comprehensive evaluation are required for diagnosis.
            </Text>
            <Text style={styles.footerText}>
              Report generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
            </Text>
          </View>
          
          {/* Logo */}
          <View style={styles.logoContainer}>
            <Image 
              src="/ai-foundation-logo.png" 
              style={styles.logo}
            />
          </View>
          
          {/* Center Logo - Only on Summary Page */}
          <View style={styles.centerLogoContainer}>
            <Image 
              src="/center.png" 
              style={styles.centerLogo}
            />
          </View>
          
          {/* Copyright */}
          <View style={styles.copyrightContainer}>
            <Text style={styles.copyrightText}>
             Software &copy; 2025 by Avyan Idris Foundation
            </Text>
            <Text style={styles.copyrightText}>
              ( NOT-FOR-PROFIT Section 8 registered Company CIN# U88100PN2025NPL242670, NGODarpan # MH/2025/0669601)
            </Text>
            <Text style={styles.copyrightText}>
              outreach@avyan.org.in
            </Text>
          </View>
          
        </Page>
      );
    
    // Individual question pages
    Object.entries(results).forEach(([questionId, questionNode]) => {
      if (!questionNode.completed) return;
      
      const questionNumber = parseInt(questionId.replace('Q', ''));
      const allResponses = getAllResponsesFromNode(questionNode, questionId);
      
      // Group responses by type for better organization
      const mainQuestion = allResponses.find(r => r.type === 'main');
      
      // Special handling for Question 1 - categorize sub-questions
      let subQuestions = allResponses.filter(r => r.type === 'sub');
      let followupQuestions = allResponses.filter(r => r.type === 'followup');
      
             if (questionId === 'Q1') {
         const zeroExampleQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'zero-examples');
         const oneExampleQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'one-examples');
         const mostOftenQuestion = allResponses.filter(r => r.type === 'followup');
         
         // For Question 1, always show all sections if they have data
         subQuestions = [...zeroExampleQuestions, ...oneExampleQuestions];
         followupQuestions = mostOftenQuestion;
               } else if (questionId === 'Q2') {
          const behaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'behavior-questions');
          const hearingTestQuestions = allResponses.filter(r => (r.type === 'sub' || r.type === 'followup') && QuestionMapping[r.id]?.category === 'hearing-test');
          
          // For Question 2, categorize into behavior questions and hearing test
          subQuestions = behaviorQuestions;
          followupQuestions = hearingTestQuestions;
        } else if (questionId === 'Q3') {
          const pretendPlayQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'pretend-play');
          
          // For Question 3, categorize into pretend play questions
          subQuestions = pretendPlayQuestions;
          followupQuestions = [];
        } else if (questionId === 'Q4') {
          const climbingQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'climbing-activities');
          
          // For Question 4, categorize into climbing activities
          subQuestions = climbingQuestions;
          followupQuestions = [];
        } else if (questionId === 'Q5') {
          const fingerMovementQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'finger-movements');
          
          // For Question 5, categorize into finger movements only
          subQuestions = fingerMovementQuestions;
          followupQuestions = allResponses.filter(r => r.type === 'followup');
        } else if (questionId === 'Q6') {
          const pointingBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'pointing-behaviors');
          const showMeQuestions = allResponses.filter(r => r.type === 'followup' && QuestionMapping[r.id]?.category === 'show-me-question');
          
          // For Question 6, categorize into pointing behaviors and show-me questions
          subQuestions = pointingBehaviorQuestions;
          followupQuestions = showMeQuestions;
        } else if (questionId === 'Q7') {
          const imitationBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'imitation-behaviors');
          const additionalCheckQuestions = allResponses.filter(r => r.type === 'followup' && QuestionMapping[r.id]?.category === 'additional-checks');
          
          // For Question 7, categorize into imitation behaviors and additional checks
          subQuestions = imitationBehaviorQuestions;
          followupQuestions = additionalCheckQuestions;
                 } else if (questionId === 'Q8') {
           const followupQuestion = allResponses.filter(r => r.type === 'followup' && QuestionMapping[r.id]?.category === 'followup-question');
           const interestCheckQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'interest-check');
           const socialBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'social-behaviors');
           const responseCheckQuestions = allResponses.filter(r => r.type === 'followup' && QuestionMapping[r.id]?.category === 'response-check');
           
           // For Question 8, categorize into followup question, interest check, social behaviors, and response check
           subQuestions = [...interestCheckQuestions, ...socialBehaviorQuestions];
           followupQuestions = [...followupQuestion, ...responseCheckQuestions];
         } else if (questionId === 'Q9') {
           const sharingBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'sharing-behaviors');
           const sharingFollowupQuestions = allResponses.filter(r => r.type === 'followup' && QuestionMapping[r.id]?.category === 'sharing-followup');
           
           // For Question 9, categorize into sharing behaviors and sharing followup
           subQuestions = sharingBehaviorQuestions;
           followupQuestions = sharingFollowupQuestions;
         } else if (questionId === 'Q10') {
           const positiveResponseQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'positive-responses');
           const negativeResponseQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'negative-responses');
           
           // For Question 10, categorize into positive responses and negative responses (similar to Q1)
           subQuestions = [...positiveResponseQuestions, ...negativeResponseQuestions];
           followupQuestions = allResponses.filter(r => r.type === 'followup');
         } else if (questionId === 'Q11') {
           const positiveBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'positive-behaviors');
           const negativeBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'negative-behaviors');
           
           // For Question 11, categorize into positive behaviors and negative behaviors (similar to Q10)
           subQuestions = [...positiveBehaviorQuestions, ...negativeBehaviorQuestions];
           followupQuestions = allResponses.filter(r => r.type === 'followup');
         } else if (questionId === 'Q12') {
           const noiseSensitivityQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'noise-sensitivity');
           const positiveBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'positive-behaviors');
           const negativeBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'negative-behaviors');
           
           // For Question 12, categorize into noise sensitivity, positive behaviors, and negative behaviors
           subQuestions = [...noiseSensitivityQuestions, ...positiveBehaviorQuestions, ...negativeBehaviorQuestions];
           followupQuestions = allResponses.filter(r => r.type === 'followup');
                   } else if (questionId === 'Q13') {
            const walkingBehaviorQuestions = allResponses.filter(r => r.type === 'followup' && QuestionMapping[r.id]?.category === 'walking-behaviors');
            
            // For Question 13, categorize into walking behaviors
            subQuestions = [];
            followupQuestions = walkingBehaviorQuestions;
          } else if (questionId === 'Q14') {
            // For Question 14, categorize by ID patterns since categories might not be working
            const eyeContactBehaviorQuestions = allResponses.filter(r => 
              r.id && (r.id.startsWith('Q14.1.') || r.id.match(/^Q14\.1\.[1-6]$/))
            );
            const eyeContactFollowupQuestions = allResponses.filter(r => 
              r.id && (r.id === 'Q14.2' || r.id === 'Q14.3')
            );
            
            // For Question 14, categorize into eye contact behaviors and follow-up questions
            subQuestions = eyeContactBehaviorQuestions;
            followupQuestions = eyeContactFollowupQuestions;
          } else if (questionId === 'Q15') {
            // For Question 15, categorize by ID patterns for copying behaviors
            const copyingBehaviorQuestions = allResponses.filter(r => 
              r.id && (r.id === 'Q15.1' || r.id === 'Q15.2' || r.id === 'Q15.3' || 
                      r.id === 'Q15.4' || r.id === 'Q15.5' || r.id === 'Q15.6')
            );
            
            // For Question 15, categorize into copying behaviors only
            subQuestions = copyingBehaviorQuestions;
            followupQuestions = [];
          } else if (questionId === 'Q17') {
            // For Question 17, categorize by ID patterns for attention-seeking behaviors
            const attentionSeekingBehaviorQuestions = allResponses.filter(r => 
              r.id && (r.id === 'Q17.1' || r.id === 'Q17.2' || r.id === 'Q17.3' || r.id === 'Q17.4')
            );
            
            // For Question 17, categorize into attention-seeking behaviors only
            subQuestions = attentionSeekingBehaviorQuestions;
            followupQuestions = [];
          } else if (questionId === 'Q18') {
            // For Question 18, categorize by ID patterns for command understanding
            const commandUnderstandingWithCluesQuestions = allResponses.filter(r => 
              r.id && (r.id === 'Q18.1' || r.id === 'Q18.2')
            );
            const commandUnderstandingWithoutCluesQuestions = allResponses.filter(r => 
              r.id && (r.id === 'Q18.3.1' || r.id === 'Q18.3.2' || r.id === 'Q18.3.3')
            );
            
            // For Question 18, categorize into command understanding with clues and without clues
            subQuestions = commandUnderstandingWithCluesQuestions;
            followupQuestions = commandUnderstandingWithoutCluesQuestions;
          } else if (questionId === 'Q19') {
            // For Question 19, categorize by ID patterns for social referencing behaviors
            const socialReferencingBehaviorQuestions = allResponses.filter(r => 
              r.id && (r.id === 'Q19.1' || r.id === 'Q19.2' || r.id === 'Q19.3')
            );
            
            // For Question 19, categorize into social referencing behaviors only
            subQuestions = socialReferencingBehaviorQuestions;
            followupQuestions = [];
          } else if (questionId === 'Q20') {
            // For Question 20, categorize by ID patterns for movement activities
            const movementEnjoymentQuestions = allResponses.filter(r => 
              r.id && r.id === 'Q20.1'
            );
            const movementReactionQuestions = allResponses.filter(r => 
              r.id && (r.id === 'Q20.2.1' || r.id === 'Q20.2.2' || r.id === 'Q20.2.3')
            );
            
            // For Question 20, categorize into movement enjoyment and movement reactions
            subQuestions = [...movementEnjoymentQuestions, ...movementReactionQuestions];
            followupQuestions = [];
          } else if (questionId === 'Q16') {
            const positiveBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'positive-behaviors');
            const negativeBehaviorQuestions = allResponses.filter(r => r.type === 'sub' && QuestionMapping[r.id]?.category === 'negative-behaviors');
            
            // For Question 16, categorize into positive behaviors and negative behaviors (similar to Q11)
            subQuestions = [...positiveBehaviorQuestions, ...negativeBehaviorQuestions];
            followupQuestions = allResponses.filter(r => r.type === 'followup');
          }
      
             pages.push(
         <Page key={questionId} size="A4" style={styles.page}>
           {/* Result in top right corner */}
           <Text 
             style={[
               styles.result, 
               questionNode.result?.toLowerCase() === 'pass' ? styles.passResult : styles.failResult
             ]}
           >
             {questionNode.result?.toUpperCase()}
           </Text>
           
           <Text style={styles.header}>M-CHAT-R Followup Assessment </Text>
           {/* <Text style={styles.questionNumber}>Question {questionNumber}</Text> */}
           
           {/* Main Question */}
           {mainQuestion && (
             <View style={styles.section}>
               <Text style={styles.mainQuestion}>{mainQuestion.title}</Text>
               <Text style={styles.mainAnswer}>Answer: {mainQuestion.answer}</Text>
             </View>
           )}
           
           {/* User Example - moved right after main question */}
           {questionNode.userExample && questionNode.userExample !== "No examples provided" && (
             <View style={styles.section}>
               <Text style={styles.userExample}>User Example:</Text>
               <Text style={styles.userExampleText}>"{questionNode.userExample}"</Text>
             </View>
           )}
           
                                    {/* Sub Questions */}
            {subQuestions.length > 0 && questionId !== 'Q1' && questionId !== 'Q2' && questionId !== 'Q3' && questionId !== 'Q4' && questionId !== 'Q5' && questionId !== 'Q6' && questionId !== 'Q7' && questionId !== 'Q8' && questionId !== 'Q9' && questionId !== 'Q10' && questionId !== 'Q11' && questionId !== 'Q12' && questionId !== 'Q13' && questionId !== 'Q14' && questionId !== 'Q15' && questionId !== 'Q16' && questionId !== 'Q17' && questionId !== 'Q18' && questionId !== 'Q19' && questionId !== 'Q20' && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                  Follow-up Questions:
                </Text>
                {/* Regular sub-questions for other questions */}
                {subQuestions.map((response) => (
                  <View key={response.id} style={styles.section}>
                    <Text style={styles.subQuestion}>{response.title}</Text>
                    <Text style={styles.subAnswer}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
           
                       {/* Special layout for Question 1 */}
            {questionId === 'Q1' && subQuestions.length > 0 && (
              <View style={styles.behaviorSection}>
                <Text style={styles.behaviorTitle}>Tell me which type of behaviours does {childName} show most often?</Text>
                <View style={styles.columnsContainer}>
                  {/* Zero Examples Column (Green Border) */}
                  <View style={[styles.column, styles.zeroExamplesColumn]}>
                    <Text style={styles.columnTitle}>Zero(0) Example - Does he/she...</Text>
                    {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'zero-examples').map((response) => (
                      <View key={response.id} style={styles.questionItem}>
                        <Text style={styles.questionText}>{response.title}</Text>
                        <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                          {response.answer}
                        </Text>
                      </View>
                    ))}
                  </View>
                  
                  {/* One Examples Column (Red Border) */}
                  <View style={[styles.column, styles.oneExamplesColumn]}>
                    <Text style={styles.columnTitle}>One(1) Example - Does he/she...</Text>
                    {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'one-examples').map((response) => (
                      <View key={response.id} style={styles.questionItem}>
                        <Text style={styles.questionText}>{response.title}</Text>
                        <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                          {response.answer}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </View>
            )}
            
            {/* Special layout for Question 2 */}
            {questionId === 'Q2' && subQuestions.length > 0 && (
              <View style={styles.behaviorQuestionsSection}>
                <Text style={styles.behaviorQuestionsTitle}>Does he/she...</Text>
                {subQuestions.map((response) => (
                  <View key={response.id} style={styles.behaviorQuestionItem}>
                    <Text style={styles.behaviorQuestionText}>{response.title}</Text>
                    <Text style={styles.behaviorAnswerText}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* Special layout for Question 3 */}
            {questionId === 'Q3' && subQuestions.length > 0 && (
              <View style={styles.pretendPlaySection}>
                <Text style={styles.pretendPlayTitle}>Pretend Play Activities</Text>
                {subQuestions.map((response) => (
                  <View key={response.id} style={styles.pretendPlayItem}>
                    <Text style={styles.pretendPlayQuestionText}>{response.title}</Text>
                    <Text style={styles.pretendPlayAnswerText}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* Special layout for Question 4 */}
            {questionId === 'Q4' && subQuestions.length > 0 && (
              <View style={styles.climbingSection}>
                <Text style={styles.climbingTitle}>Climbing Activities</Text>
                {subQuestions.map((response) => (
                  <View key={response.id} style={styles.climbingItem}>
                    <Text style={styles.climbingQuestionText}>{response.title}</Text>
                    <Text style={styles.climbingAnswerText}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
                         {/* Special layout for Question 5 */}
             {questionId === 'Q5' && (
               <>
                 {/* Finger Movements Section */}
                 {subQuestions.length > 0 && (
                   <View style={styles.fingerMovementsSection}>
                     <Text style={styles.fingerMovementsTitle}>Does he/she...</Text>
                     <View style={styles.fingerMovementsColumnsContainer}>
                       {/* Green Column */}
                       <View style={[styles.fingerMovementsColumn, styles.greenColumn]}>
                         <Text style={styles.fingerMovementsColumnTitle}>Does he/she...</Text>
                         {subQuestions.slice(0, 2).map((response) => (
                           <View key={response.id} style={styles.fingerMovementsItem}>
                             <Text style={styles.fingerMovementsQuestionText}>{response.title}</Text>
                             <Text style={[styles.fingerMovementsAnswerText, response.answer === 'YES' ? styles.greenAnswer : styles.noAnswer]}>
                               {response.answer}
                             </Text>
                           </View>
                         ))}
                       </View>
                       
                       {/* Yellow Column */}
                       <View style={[styles.fingerMovementsColumn, styles.yellowColumn]}>
                         <Text style={styles.fingerMovementsColumnTitle}>Does he/she...</Text>
                         {subQuestions.slice(2).map((response) => (
                           <View key={response.id} style={styles.fingerMovementsItem}>
                             <Text style={styles.fingerMovementsQuestionText}>{response.title}</Text>
                             <Text style={[styles.fingerMovementsAnswerText, response.answer === 'YES' ? styles.yellowAnswer : styles.noAnswer]}>
                               {response.answer}
                             </Text>
                           </View>
                         ))}
                       </View>
                     </View>
                   </View>
                 )}
                 
                 {/* Follow-up Question Section */}
                 {followupQuestions.length > 0 && (
                   <View style={styles.frequencySection}>
                     <Text style={styles.frequencyTitle}>Additional Questions</Text>
                     {followupQuestions.map((response) => (
                       <View key={response.id} style={styles.frequencyItem}>
                         <Text style={styles.frequencyQuestionText}>{response.title}</Text>
                         <Text style={styles.frequencyAnswerText}>Answer: {response.answer}</Text>
                       </View>
                     ))}
                   </View>
                 )}
               </>
             )}
            
            {/* Special layout for Question 6 */}
            {questionId === 'Q6' && subQuestions.length > 0 && (
              <View style={styles.pointingBehaviorsSection}>
                <Text style={styles.pointingBehaviorsTitle}>Does he/she...</Text>
                {subQuestions.map((response) => (
                  <View key={response.id} style={styles.pointingBehaviorsItem}>
                    <Text style={styles.pointingBehaviorsQuestionText}>{response.title}</Text>
                    <Text style={styles.pointingBehaviorsAnswerText}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* Special layout for Question 7 */}
            {questionId === 'Q7' && subQuestions.length > 0 && (
              <View style={styles.imitationBehaviorsSection}>
                <Text style={styles.imitationBehaviorsTitle}>Imitation Behaviors</Text>
                {subQuestions.map((response) => (
                  <View key={response.id} style={styles.imitationBehaviorsItem}>
                    <Text style={styles.imitationBehaviorsQuestionText}>{response.title}</Text>
                    <Text style={styles.imitationBehaviorsAnswerText}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
                         {/* Special layout for Question 8 */}
             {questionId === 'Q8' && (
               <>
                 {/* Follow-up Question Section - Show first */}
                 {followupQuestions.filter(r => QuestionMapping[r.id]?.category === 'followup-question').length > 0 && (
                   <View style={styles.followupQuestionSection}>
                     <Text style={styles.followupQuestionTitle}>Follow-up Question</Text>
                     {followupQuestions.filter(r => QuestionMapping[r.id]?.category === 'followup-question').map((response) => (
                       <View key={response.id} style={styles.followupQuestionItem}>
                         <Text style={styles.followupQuestionText}>{response.title}</Text>
                         <Text style={styles.followupQuestionAnswerText}>Answer: {response.answer}</Text>
                       </View>
                     ))}
                   </View>
                 )}
                 
                 {/* Social Behaviors Section - Show second */}
                 {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'social-behaviors').length > 0 && (
                   <View style={styles.socialBehaviorsSection}>
                     <Text style={styles.socialBehaviorsTitle}>Social Behaviors</Text>
                     {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'social-behaviors').map((response) => (
                       <View key={response.id} style={styles.socialBehaviorsItem}>
                         <Text style={styles.socialBehaviorsQuestionText}>{response.title}</Text>
                         <Text style={styles.socialBehaviorsAnswerText}>Answer: {response.answer}</Text>
                       </View>
                     ))}
                   </View>
                 )}
               </>
             )}
             
                           {/* Special layout for Question 9 */}
              {questionId === 'Q9' && (
                <>
                  {/* Sharing Behaviors Section */}
                  {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'sharing-behaviors').length > 0 && (
                    <View style={styles.sharingBehaviorsSection}>
                      <Text style={styles.sharingBehaviorsTitle}>Does he/she sometimes bring you...</Text>
                      {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'sharing-behaviors').map((response) => (
                        <View key={response.id} style={styles.sharingBehaviorsItem}>
                          <Text style={styles.sharingBehaviorsQuestionText}>{response.title}</Text>
                          <Text style={styles.sharingBehaviorsAnswerText}>Answer: {response.answer}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </>
              )}
              
                             {/* Special layout for Question 10 - Similar to Question 1 */}
               {questionId === 'Q10' && subQuestions.length > 0 && (
                 <View style={styles.behaviorSection}>
                   <Text style={styles.behaviorTitle}>Tell me which type of behaviours does {childName} show most often?</Text>
                   <View style={styles.columnsContainer}>
                     {/* Zero Examples Column (Green Border) */}
                     <View style={[styles.column, styles.zeroExamplesColumn]}>
                       <Text style={styles.columnTitle}>Zero(0) Example - Does he/she...</Text>
                       {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'positive-responses').map((response) => (
                         <View key={response.id} style={styles.questionItem}>
                           <Text style={styles.questionText}>{response.title}</Text>
                           <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                             {response.answer}
                           </Text>
                         </View>
                       ))}
                     </View>
                     
                     {/* One Examples Column (Red Border) */}
                     <View style={[styles.column, styles.oneExamplesColumn]}>
                       <Text style={styles.columnTitle}>One(1) Example - Does he/she...</Text>
                       {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'negative-responses').map((response) => (
                         <View key={response.id} style={styles.questionItem}>
                           <Text style={styles.questionText}>{response.title}</Text>
                           <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                             {response.answer}
                           </Text>
                         </View>
                       ))}
                     </View>
                   </View>
                 </View>
               )}
               
               {/* Special layout for Question 11 - Similar to Question 10 */}
               {questionId === 'Q11' && subQuestions.length > 0 && (
                 <View style={styles.behaviorSection}>
                   <Text style={styles.behaviorTitle}>Tell me which type of behaviours does {childName} show most often?</Text>
                   <View style={styles.columnsContainer}>
                     {/* Zero Examples Column (Green Border) */}
                     <View style={[styles.column, styles.zeroExamplesColumn]}>
                       <Text style={styles.columnTitle}>Zero(0) Example - Does he/she...</Text>
                       {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'positive-behaviors').map((response) => (
                         <View key={response.id} style={styles.questionItem}>
                           <Text style={styles.questionText}>{response.title}</Text>
                           <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                             {response.answer}
                           </Text>
                         </View>
                       ))}
                     </View>
                     
                     {/* One Examples Column (Red Border) */}
                     <View style={[styles.column, styles.oneExamplesColumn]}>
                       <Text style={styles.columnTitle}>One(1) Example - Does he/she...</Text>
                       {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'negative-behaviors').map((response) => (
                         <View key={response.id} style={styles.questionItem}>
                           <Text style={styles.questionText}>{response.title}</Text>
                           <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                             {response.answer}
                           </Text>
                         </View>
                       ))}
                     </View>
                   </View>
                 </View>
               )}
               
               {/* Special layout for Question 12 - Complex structure with noise sensitivity and behaviors */}
               {questionId === 'Q12' && subQuestions.length > 0 && (
                 <>
                   {/* Noise Sensitivity Section */}
                   {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'noise-sensitivity').length > 0 && (
                     <View style={styles.noiseSensitivitySection}>
                       <Text style={styles.noiseSensitivityTitle}>Does {childName} have a negative reaction to the sound of...</Text>
                       {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'noise-sensitivity').map((response) => (
                         <View key={response.id} style={styles.noiseSensitivityItem}>
                           <Text style={styles.noiseSensitivityQuestionText}>{response.title}</Text>
                           <Text style={styles.noiseSensitivityAnswerText}>Answer: {response.answer}</Text>
                         </View>
                       ))}
                     </View>
                   )}
                   
                   {/* Behavior Section */}
                   {(subQuestions.filter(r => QuestionMapping[r.id]?.category === 'positive-behaviors').length > 0 || 
                     subQuestions.filter(r => QuestionMapping[r.id]?.category === 'negative-behaviors').length > 0) && (
                     <View style={styles.behaviorSection}>
                       <Text style={styles.behaviorTitle}>Tell me which type of behaviours does {childName} show most often?</Text>
                       <View style={styles.columnsContainer}>
                         {/* Zero Examples Column (Green Border) */}
                         <View style={[styles.column, styles.zeroExamplesColumn]}>
                           <Text style={styles.columnTitle}>Zero(0) Example - Does he/she...</Text>
                           {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'positive-behaviors').map((response) => (
                             <View key={response.id} style={styles.questionItem}>
                               <Text style={styles.questionText}>{response.title}</Text>
                               <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                                 {response.answer}
                               </Text>
                             </View>
                           ))}
                         </View>
                         
                         {/* One Examples Column (Red Border) */}
                         <View style={[styles.column, styles.oneExamplesColumn]}>
                           <Text style={styles.columnTitle}>One(1) Example - Does he/she...</Text>
                           {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'negative-behaviors').map((response) => (
                             <View key={response.id} style={styles.questionItem}>
                               <Text style={styles.questionText}>{response.title}</Text>
                               <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                                 {response.answer}
                               </Text>
                             </View>
                           ))}
                         </View>
                       </View>
                     </View>
                   )}
                 </>
                               )}
                
                {/* Special layout for Question 13 - Walking behaviors */}
                {questionId === 'Q13' && followupQuestions.length > 0 && (
                  <View style={styles.walkingBehaviorsSection}>
                    <Text style={styles.walkingBehaviorsTitle}>Walking Assessment</Text>
                    {followupQuestions.map((response) => (
                      <View key={response.id} style={styles.walkingBehaviorsItem}>
                        <Text style={styles.walkingBehaviorsQuestionText}>{response.title}</Text>
                        <Text style={styles.walkingBehaviorsAnswerText}>Answer: {response.answer}</Text>
                      </View>
                    ))}
                  </View>
                )}
                
                {/* Special layout for Question 14 - Eye contact behaviors and follow-up questions */}
                {questionId === 'Q14' && (
                  <>
                    {/* Eye Contact Behaviors Section */}
                    {subQuestions.length > 0 && (
                      <View style={styles.eyeContactBehaviorsSection}>
                        <Text style={styles.eyeContactBehaviorsTitle}>Does {childName} look you in the eye...</Text>
                        {subQuestions.map((response) => (
                          <View key={response.id} style={styles.eyeContactBehaviorsItem}>
                            <Text style={styles.eyeContactBehaviorsQuestionText}>{response.title}</Text>
                            <Text style={styles.eyeContactBehaviorsAnswerText}>Answer: {response.answer}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                    
                    {/* Eye Contact Follow-up Questions Section */}
                    {followupQuestions.length > 0 && (
                      <View style={styles.eyeContactFollowupSection}>
                        <Text style={styles.eyeContactFollowupTitle}>Follow-up Questions</Text>
                        {followupQuestions.map((response) => (
                          <View key={response.id} style={styles.eyeContactFollowupItem}>
                            <Text style={styles.eyeContactFollowupQuestionText}>{response.title}</Text>
                            <Text style={styles.eyeContactFollowupAnswerText}>Answer: {response.answer}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </>
                )}
                
                {/* Special layout for Question 15 - Copying behaviors */}
                {questionId === 'Q15' && subQuestions.length > 0 && (
                  <View style={styles.copyingBehaviorsSection}>
                    <Text style={styles.copyingBehaviorsTitle}>Does {childName} try to copy you if you...</Text>
                    {subQuestions.map((response) => (
                      <View key={response.id} style={styles.copyingBehaviorsItem}>
                        <Text style={styles.copyingBehaviorsQuestionText}>{response.title}</Text>
                        <Text style={styles.copyingBehaviorsAnswerText}>Answer: {response.answer}</Text>
                      </View>
                    ))}
                  </View>
                )}
                
                {/* Special layout for Question 17 - Attention-seeking behaviors */}
                {questionId === 'Q17' && subQuestions.length > 0 && (
                  <View style={styles.attentionSeekingBehaviorsSection}>
                    <Text style={styles.attentionSeekingBehaviorsTitle}>Does he...</Text>
                    {subQuestions.map((response) => (
                      <View key={response.id} style={styles.attentionSeekingBehaviorsItem}>
                        <Text style={styles.attentionSeekingBehaviorsQuestionText}>{response.title}</Text>
                        <Text style={styles.attentionSeekingBehaviorsAnswerText}>Answer: {response.answer}</Text>
                      </View>
                    ))}
                  </View>
                )}
                
                {/* Special layout for Question 18 - Command understanding */}
                {questionId === 'Q18' && (
                  <>
                    {/* Command Understanding with Clues Section */}
                    {subQuestions.length > 0 && (
                      <View style={styles.commandUnderstandingWithCluesSection}>
                        <Text style={styles.commandUnderstandingWithCluesTitle}>When the situation gives him a clue, can he follow a command?</Text>
                        {subQuestions.map((response) => (
                          <View key={response.id} style={styles.commandUnderstandingWithCluesItem}>
                            <Text style={styles.commandUnderstandingWithCluesQuestionText}>{response.title}</Text>
                            <Text style={styles.commandUnderstandingWithCluesAnswerText}>Answer: {response.answer}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                    
                    {/* Command Understanding without Clues Section */}
                    {followupQuestions.length > 0 && (
                      <View style={styles.commandUnderstandingWithoutCluesSection}>
                        <Text style={styles.commandUnderstandingWithoutCluesTitle}>When the situation does not give any clues, can he follow a command?</Text>
                        {followupQuestions.map((response) => (
                          <View key={response.id} style={styles.commandUnderstandingWithoutCluesItem}>
                            <Text style={styles.commandUnderstandingWithoutCluesQuestionText}>{response.title}</Text>
                            <Text style={styles.commandUnderstandingWithoutCluesAnswerText}>Answer: {response.answer}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </>
                )}
                
                {/* Special layout for Question 19 - Social referencing behaviors */}
                {questionId === 'Q19' && subQuestions.length > 0 && (
                  <View style={styles.socialReferencingBehaviorsSection}>
                    <Text style={styles.socialReferencingBehaviorsTitle}>Social Referencing Behaviors</Text>
                    {subQuestions.map((response) => (
                      <View key={response.id} style={styles.socialReferencingBehaviorsItem}>
                        <Text style={styles.socialReferencingBehaviorsQuestionText}>{response.title}</Text>
                        <Text style={styles.socialReferencingBehaviorsAnswerText}>Answer: {response.answer}</Text>
                      </View>
                    ))}
                  </View>
                )}
                
                {/* Special layout for Question 20 - Movement activities */}
                {questionId === 'Q20' && (
                  <>
                    {/* Movement Enjoyment Section */}
                    {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'movement-enjoyment').length > 0 && (
                      <View style={styles.movementEnjoymentSection}>
                        <Text style={styles.movementEnjoymentTitle}>Follow-up Question</Text>
                        {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'movement-enjoyment').map((response) => (
                          <View key={response.id} style={styles.movementEnjoymentItem}>
                            <Text style={styles.movementEnjoymentQuestionText}>{response.title}</Text>
                            <Text style={styles.movementEnjoymentAnswerText}>Answer: {response.answer}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                    
                    {/* Movement Reactions Section */}
                    {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'movement-reactions').length > 0 && (
                      <View style={styles.movementReactionsSection}>
                        <Text style={styles.movementReactionsTitle}>When you swing or bounce him, how does he react?</Text>
                        {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'movement-reactions').map((response) => (
                          <View key={response.id} style={styles.movementReactionsItem}>
                            <Text style={styles.movementReactionsQuestionText}>{response.title}</Text>
                            <Text style={styles.movementReactionsAnswerText}>Answer: {response.answer}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </>
                )}
                
                {/* Special layout for Question 16 - Similar to Question 11 */}
               {questionId === 'Q16' && subQuestions.length > 0 && (
                 <View style={styles.behaviorSection}>
                   <Text style={styles.behaviorTitle}>Tell me which type of behaviours does {childName} show most often?</Text>
                   <View style={styles.columnsContainer}>
                     {/* Zero Examples Column (Green Border) */}
                     <View style={[styles.column, styles.zeroExamplesColumn]}>
                       <Text style={styles.columnTitle}>Zero(0) Example - Does he/she...</Text>
                       {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'positive-behaviors').map((response) => (
                         <View key={response.id} style={styles.questionItem}>
                           <Text style={styles.questionText}>{response.title}</Text>
                           <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                             {response.answer}
                           </Text>
                         </View>
                       ))}
                     </View>
                     
                     {/* One Examples Column (Red Border) */}
                     <View style={[styles.column, styles.oneExamplesColumn]}>
                       <Text style={styles.columnTitle}>One(1) Example - Does he/she...</Text>
                       {subQuestions.filter(r => QuestionMapping[r.id]?.category === 'negative-behaviors').map((response) => (
                         <View key={response.id} style={styles.questionItem}>
                           <Text style={styles.questionText}>{response.title}</Text>
                           <Text style={[styles.answerText, response.answer === 'YES' ? styles.yesAnswer : styles.noAnswer]}>
                             {response.answer}
                           </Text>
                         </View>
                       ))}
                     </View>
                   </View>
                 </View>
               )}
              
                             {/* Special layout for Question 10 Follow-up */}
               {followupQuestions.length > 0 && questionId === 'Q10' && (
                 <View style={styles.mostOftenSection}>
                   <Text style={styles.mostOftenTitle}>Most Often Choice:</Text>
                   {followupQuestions.map((response) => (
                     <View key={response.id} style={styles.section}>
                       <Text style={styles.mostOftenSubtitle}>{response.title}</Text>
                       <Text style={styles.mostOftenAnswer}>Answer: {response.answer}</Text>
                     </View>
                   ))}
                 </View>
               )}
               
               {/* Special layout for Question 11 Follow-up */}
               {followupQuestions.length > 0 && questionId === 'Q11' && (
                 <View style={styles.mostOftenSection}>
                   <Text style={styles.mostOftenTitle}>Most Often Choice:</Text>
                   {followupQuestions.map((response) => (
                     <View key={response.id} style={styles.section}>
                       <Text style={styles.mostOftenSubtitle}>{response.title}</Text>
                       <Text style={styles.mostOftenAnswer}>Answer: {response.answer}</Text>
                     </View>
                   ))}
                 </View>
               )}
               
               {/* Special layout for Question 12 Follow-up */}
               {followupQuestions.length > 0 && questionId === 'Q12' && (
                 <View style={styles.mostOftenSection}>
                   <Text style={styles.mostOftenTitle}>Most Often Choice:</Text>
                   {followupQuestions.map((response) => (
                     <View key={response.id} style={styles.section}>
                       <Text style={styles.mostOftenSubtitle}>{response.title}</Text>
                       <Text style={styles.mostOftenAnswer}>Answer: {response.answer}</Text>
                     </View>
                   ))}
                 </View>
               )}
               
               {/* Special layout for Question 16 Follow-up */}
               {followupQuestions.length > 0 && questionId === 'Q16' && (
                 <View style={styles.mostOftenSection}>
                   <Text style={styles.mostOftenTitle}>Most Often Choice:</Text>
                   {followupQuestions.map((response) => (
                     <View key={response.id} style={styles.section}>
                       <Text style={styles.mostOftenSubtitle}>{response.title}</Text>
                       <Text style={styles.mostOftenAnswer}>Answer: {response.answer}</Text>
                     </View>
                   ))}
                 </View>
               )}
           
            {/* Enhanced Most Often Choice Section */}
            {followupQuestions.length > 0 && questionId === 'Q1' && (
              <View style={styles.mostOftenSection}>
                <Text style={styles.mostOftenTitle}>Most Often Choice:</Text>
                {followupQuestions.map((response) => (
                  <View key={response.id} style={styles.section}>
                    <Text style={styles.mostOftenSubtitle}>{response.title}</Text>
                    <Text style={styles.mostOftenAnswer}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* Special layout for Question 2 Hearing Test */}
            {followupQuestions.length > 0 && questionId === 'Q2' && (
              <View style={styles.hearingTestSection}>
                <Text style={styles.hearingTestTitle}>Universal Hearing Test</Text>
                <Text style={styles.hearingTestSubtitle}>(This is a universal test for all children and does not affect the assessment result)</Text>
                {followupQuestions.map((response) => (
                  <View key={response.id} style={styles.hearingTestItem}>
                    <Text style={styles.hearingTestQuestionText}>{response.title}</Text>
                    <Text style={styles.hearingTestAnswerText}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* Special layout for Question 6 Follow-up */}
            {followupQuestions.length > 0 && questionId === 'Q6' && (
              <View style={styles.showMeSection}>
                <Text style={styles.showMeTitle}>Follow-up Question</Text>
                {followupQuestions.map((response) => (
                  <View key={response.id} style={styles.showMeItem}>
                    <Text style={styles.showMeQuestionText}>{response.title}</Text>
                    <Text style={styles.showMeAnswerText}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* Special layout for Question 7 Follow-up */}
            {followupQuestions.length > 0 && questionId === 'Q7' && (
              <View style={styles.additionalChecksSection}>
                <Text style={styles.additionalChecksTitle}>Additional Checks</Text>
                {followupQuestions.map((response) => (
                  <View key={response.id} style={styles.additionalChecksItem}>
                    <Text style={styles.additionalChecksQuestionText}>{response.title}</Text>
                    <Text style={styles.additionalChecksAnswerText}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
            
                         {/* Special layout for Question 8 Follow-up - Frequency Question */}
             {followupQuestions.length > 0 && questionId === 'Q8' && (
               <>
                 {/* Response Check Section */}
                 {followupQuestions.filter(r => QuestionMapping[r.id]?.category === 'response-check').length > 0 && (
                   <View style={styles.responseCheckSection}>
                     <Text style={styles.responseCheckTitle}>Frequency Question</Text>
                     {followupQuestions.filter(r => QuestionMapping[r.id]?.category === 'response-check').map((response) => (
                       <View key={response.id} style={styles.responseCheckItem}>
                         <Text style={styles.responseCheckQuestionText}>{response.title}</Text>
                         <Text style={styles.responseCheckAnswerText}>Answer: {response.answer}</Text>
                       </View>
                     ))}
                   </View>
                 )}
               </>
             )}
             
             {/* Special layout for Question 9 Follow-up */}
             {followupQuestions.length > 0 && questionId === 'Q9' && (
               <>
                 {/* Sharing Follow-up Section */}
                 {followupQuestions.filter(r => QuestionMapping[r.id]?.category === 'sharing-followup').length > 0 && (
                   <View style={styles.sharingFollowupSection}>
                     <Text style={styles.sharingFollowupTitle}>Follow-up Question</Text>
                     {followupQuestions.filter(r => QuestionMapping[r.id]?.category === 'sharing-followup').map((response) => (
                       <View key={response.id} style={styles.sharingFollowupItem}>
                         <Text style={styles.sharingFollowupQuestionText}>{response.title}</Text>
                         <Text style={styles.sharingFollowupAnswerText}>Answer: {response.answer}</Text>
                       </View>
                     ))}
                   </View>
                 )}
               </>
             )}
            

            

            

           
                                    {/* Regular Follow-up Questions for other questions */}
                                                                                                               {followupQuestions.length > 0 && questionId !== 'Q1' && questionId !== 'Q2' && questionId !== 'Q3' && questionId !== 'Q4' && questionId !== 'Q5' && questionId !== 'Q6' && questionId !== 'Q7' && questionId !== 'Q8' && questionId !== 'Q9' && questionId !== 'Q10' && questionId !== 'Q11' && questionId !== 'Q12' && questionId !== 'Q13' && questionId !== 'Q14' && questionId !== 'Q15' && questionId !== 'Q16' && questionId !== 'Q17' && questionId !== 'Q18' && questionId !== 'Q19' && questionId !== 'Q20' && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                  Additional Questions:
                </Text>
                {followupQuestions.map((response) => (
                  <View key={response.id} style={styles.section}>
                    <Text style={styles.subQuestion}>{response.title}</Text>
                    <Text style={styles.subAnswer}>Answer: {response.answer}</Text>
                  </View>
                ))}
              </View>
            )}
           
           <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
           
                                               {/* Logo */}
             <View style={styles.logoContainer}>
               <Image 
                 src="/ai-foundation-logo.png" 
                 style={styles.logo}
               />
             </View>
             
                           {/* Copyright */}
              <View style={styles.copyrightContainer}>
                <Text style={styles.copyrightText}> 
                  © 2025 by Avyan Idris Foundation
                </Text>
                <Text style={styles.copyrightText}>
                  (We're NOT-FOR-PROFIT Section 8 registered Company CIN# U88100PN2025NPL242670, NGODarpan # MH/2025/0669601)
                </Text>
                <Text style={styles.copyrightText}>
                  Tel: +91 94030 12324 Email : outreach@avyan.org.in
                </Text>
              </View>
         </Page>
       );
    });
    
    return pages;
  };

  return (
    <Document>
      {generatePages()}
    </Document>
  );
};

export default SimpleResultPDF;
